import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './ChatBotComponent.module.css';
interface Props {
  open: boolean;
  onClose: () => void;
}

// 🔹 Simple gradient section title
const SectionTitle: React.FC<{ number?: string; title: string }> = ({ number, title }) => (
  <Box
    display="flex"
    alignItems="center"
    mb={1.2}
    mt={2.2}
    sx={{
      background: 'linear-gradient(90deg, rgba(100,149,237,0.06) 0%, rgba(138,43,226,0.03) 100%)',
      borderRadius: 1,
      px: 1,
      py: 0.5,
    }}
  >
    {number && (
      <Chip
        label={number}
        size="small"
        sx={{
          mr: 1,
          fontWeight: 600,
          color: 'white',
          background: 'linear-gradient(135deg, #6C63FF 0%, #5AC8FA 100%)',
        }}
      />
    )}
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 600,
        background: 'linear-gradient(45deg, #6C63FF, #5AC8FA)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}
    >
      {title}
    </Typography>
  </Box>
);

const UserGuideUaeMt: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          backgroundColor: '#fafafa',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header with Close Icon */}
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.1rem',
          color: 'white',
          py: 1.2,
          pl: 2,
          pr: 5,
          textAlign: 'center',
          background: 'linear-gradient(90deg, #6C63FF 0%, #5AC8FA 100%)',
          position: 'relative',
          letterSpacing: 0.2,
          lineHeight: 1.4,
        }}
      >
        Dialogue with Data — User Guide & Guidelines (Middle East)

        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={onClose}

          sx={{
            position: 'absolute',
            right: 8,
            top: 6,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.25)',
            },
          }}
        >
          <CloseIcon className={styles.closeIcon}/>
        </IconButton>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent
        dividers
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2.5,
          py: 1.5,
          backgroundColor: '#ffffff',
        }}
      >
        <SectionTitle title="Purpose" />
        <Typography paragraph sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
          This guide helps you use <strong>Dialogue with Data</strong> effectively — understanding
          what it can and can’t do, how to ask the right questions, and what to expect in responses.
          Take a few minutes to read it before you start chatting with your data.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="1" title="At a Glance" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          <strong>What it is:</strong> A conversational analytics assistant that lets you query data
          (EPOS + Merchandising + Sell-In + Leaflet) in plain English and get short, accurate insights.
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          <strong>Who it’s for:</strong> Anyone who needs quick answers without SQL.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="2" title="Data Coverage (What’s Available)" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          DwD combines Sales (EPOS), Sell-In, Merchandising, and Leaflet data in one place.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8 }}>
          Key Dimensions & Metrics:
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, color: 'text.secondary', fontSize: '0.88rem' }}>
          <li><strong>Time & Location:</strong> Year, Month, Week, Region, City, Channel</li>
          <li><strong>Retailer & Store:</strong> Retailer, Store Name, PEP Store Code</li>
          <li><strong>Product Details:</strong> SKU ID, Barcode, Description, Brand, etc.</li>
          <li><strong>Sales Metrics:</strong> Promo/Non-Promo Sales, Volume, Transactions</li>
          <li><strong>Sell-In & Leaflet:</strong> Volume, Transactions, Net Sales</li>
          <li><strong>Merchandising:</strong> Share of Shelf (SOS), Planogram Compliance, OOS</li>
          <li><strong>Promo Leaflet:</strong> Depth of Promotions (DOP), Share of Facings (SOF)</li>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          💡 DwD only uses listed fields or KPIs derived from them. Missing fields are ignored.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle title="Key Calculated Metrics" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          Revenue Share, Promo Contribution (%), Weighted Distribution (WD), Numeric Distribution (ND),
          Average Price, Rate of Sale (ROS), Off-Shelf Share (OSS), Share of Shelf (SOS),
          Planogram Compliance, Out of Stock (OOS %), Share of Facings, Depth of Promotions.
        </Typography>

        <SectionTitle title="KPI — Recommended Granularity & Examples" />
        <Typography component="div" paragraph sx={{ fontSize: '0.9rem' }}>
          <ul>
            <li><strong>Off Shelf Share (OSS):</strong> Store level — “OSS for store XYZ in DXB, March 2025”</li>
            <li><strong>Share of Shelf (SOS):</strong> Brand level — “SOS for Lays in Dubai last month”</li>
            <li><strong>Depth of Promotions (DOP):</strong> Retailer/Brand — “DOP for Lays at Lulu last month”</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="3" title="What DwD Can Do" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          ✅ Answer numeric questions, return top lists, generate visuals, and display SQL queries for transparency.
        </Typography>

        <SectionTitle number="4" title="What the Bot Cannot Do" />
        <Typography paragraph color="text.secondary" sx={{ fontSize: '0.9rem' }}>
          ❌ Root-cause analysis, predictive modeling, editing data, or using non-listed fields.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="5" title="How to Ask Good Questions" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          🧭 Use: <strong>[Metric] + [Time Period] + [Dimension/Filter]</strong>. Keep one metric per question.
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          Examples: “Total_sales for PepsiCo in Dubai Q2 2025”, “Top 5 SKUs by promo_sales in June 2025”.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="7" title="Visualization" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          📊 DwD can automatically create simple charts. You can also say: “Show this as a chart.”
        </Typography>

        <SectionTitle number="8" title="SQL Visibility" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          🧾 The SQL query used for your answer is visible in the UI. Review before reuse.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="9" title="Common Issues & Fixes" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          ⚠️ Use explicit timeframes, check field existence, and review SQL for unexpected results.
        </Typography>

        <SectionTitle number="11" title="FAQ" />
        <Typography paragraph component="div" sx={{ fontSize: '0.9rem' }}>
          <strong>Q:</strong> Can it compare with last year?<br />
          <strong>A:</strong> Yes — “Compare July 2025 vs July 2024.”<br /><br />
          <strong>Q:</strong> Can it explain why performance changed?<br />
          <strong>A:</strong> Not yet — it shows what happened, not why.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <SectionTitle number="12" title="Contact & Feedback" />
        <Typography paragraph sx={{ fontSize: '0.9rem' }}>
          🙌 Thanks for helping improve Dialogue with Data! If you find missing metrics or incorrect outputs,
          please reach out to the team.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Final note: DwD helps you validate facts quickly — your friendly data partner for fast insights.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default UserGuideUaeMt;
